import type { ComponentType } from 'react';

import BookmarkRoundedIcon from '@mui/icons-material/BookmarkRounded';
import BalanceRoundedIcon from '@mui/icons-material/BalanceRounded';
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded';
import MarkChatUnreadRoundedIcon from '@mui/icons-material/MarkChatUnreadRounded';
import EmojiEventsRoundedIcon from '@mui/icons-material/EmojiEventsRounded';
import GavelRoundedIcon from '@mui/icons-material/GavelRounded';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import NotificationsRoundedIcon from '@mui/icons-material/NotificationsRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import PublicRoundedIcon from '@mui/icons-material/PublicRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import type { SvgIconProps } from '@mui/material/SvgIcon';

import type { PublicUser } from '@/lib/schemas/user';

export interface NavItem {
  /** Locale key, resolved where the item is rendered (spec §8.9). */
  labelKey: string;
  to: string;
  Icon: ComponentType<SvgIconProps>;
  /** Hidden from signed-out visitors. */
  requiresAuth?: boolean;
  /** Hidden from anyone whose role is not in this list. */
  requiresRole?: Array<PublicUser['role']>;
  /** Hidden from a personal account — there is no business for it to be about. */
  requiresBusiness?: boolean;
  /**
   * Hidden unless the business's plan includes this.
   *
   * Unlike TierGate, which shows a paid feature and says what it would take,
   * navigation is a list of places to go: an entry that always leads to a
   * refusal is a worse advertisement for a plan than no entry at all. The
   * billing tab is where a plan is sold.
   */
  requiresCapability?: string;
  /** Shown in the mobile bottom bar (spec §9.3 allows only five slots there). */
  onBottomBar?: boolean;
}

export const NAV_ITEMS: NavItem[] = [
  { labelKey: 'nav.home', to: '/', Icon: HomeRoundedIcon, onBottomBar: true },
  { labelKey: 'nav.search', to: '/search', Icon: SearchRoundedIcon, onBottomBar: true },
  { labelKey: 'nav.map', to: '/map', Icon: PublicRoundedIcon },
  { labelKey: 'nav.hallOfFame', to: '/ideals', Icon: EmojiEventsRoundedIcon },
  // Public, and deliberately not hidden in a footer nobody reads: the numbers
  // are only an answer to "you are biased" if somebody can find them without
  // being told where they are (spec §11).
  { labelKey: 'nav.transparency', to: '/transparency', Icon: BalanceRoundedIcon },
  {
    labelKey: 'nav.notifications',
    to: '/notifications',
    Icon: NotificationsRoundedIcon,
    requiresAuth: true,
    onBottomBar: true,
  },
  // Where a business finds what is waiting for an answer — a list of posts,
  // not the settings panel where watch terms are configured.
  {
    labelKey: 'nav.toAnswer',
    to: '/inbox',
    Icon: MarkChatUnreadRoundedIcon,
    requiresAuth: true,
    requiresBusiness: true,
  },
  {
    labelKey: 'nav.dashboard',
    to: '/dashboard',
    Icon: InsightsRoundedIcon,
    requiresAuth: true,
    requiresBusiness: true,
    requiresCapability: 'ANALYTICS',
  },
  { labelKey: 'nav.bookmarks', to: '/bookmarks', Icon: BookmarkRoundedIcon, requiresAuth: true },
  {
    labelKey: 'nav.profile',
    to: '/profile',
    Icon: PersonRoundedIcon,
    requiresAuth: true,
    onBottomBar: true,
  },
  {
    labelKey: 'nav.moderation',
    to: '/admin',
    Icon: GavelRoundedIcon,
    requiresAuth: true,
    requiresRole: ['MODERATOR', 'ADMIN'],
  },
];

export interface NavAudience {
  isSignedIn: boolean;
  role?: PublicUser['role'];
  /** The business this account speaks for, if any. */
  businessSlug?: string | null;
  /** What that business's plan currently allows. */
  capabilities?: string[];
}

export function visibleNavItems(audience: NavAudience): NavItem[] {
  const { isSignedIn, role, businessSlug, capabilities = [] } = audience;

  return NAV_ITEMS.filter((item) => {
    if (item.requiresAuth && !isSignedIn) return false;
    // No role means no role-gated links — including while the session is still
    // resolving, which keeps the rail from flickering a moderator link into
    // existence for everyone on first paint. The same reasoning covers the
    // business links: absent capabilities hide them rather than guessing.
    if (item.requiresRole) return role !== undefined && item.requiresRole.includes(role);
    if (item.requiresBusiness && !businessSlug) return false;
    if (item.requiresCapability && !capabilities.includes(item.requiresCapability)) return false;
    return true;
  });
}
